import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import { PERMISSIONS, type AccountNotification } from '@/api/types';
import { NOTIFICATIONS_KEY, notificationLink } from '@/components/AccountNotifications';
import { Select } from '@/components/Select';
import { PageHeader, Section } from '@/components/ops/Layout';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { MutationError, QueryState } from '@/components/ops/Problem';
import { formatTimestamp } from '@/ops/format';
import { formatWhen } from './AccountsPage';

/** The kinds the collectors detect (AccountEventKinds), in words. */
export const KIND_LABELS: Record<string, string> = {
  tenant_added: 'New accounts',
  user_added: 'People joined',
  user_removed: 'People removed',
  first_login: 'First sign-ins',
  user_role_changed: 'Role changes',
  app_enabled: 'Apps switched on',
  plan_changed: 'Plan changes',
  subscription_status_changed: 'Subscription status',
  tenant_deletion_requested: 'Deletions asked for',
  tenant_deletion_approved: 'Deletions approved',
  tenant_backup_ready: 'Backups ready',
  tenant_deleted: 'Tenants deleted',
};

/** Notifications grouped by calendar day (local), newest day first; the order inside a day is kept. */
export function groupByDay(items: AccountNotification[]): { day: string; items: AccountNotification[] }[] {
  const groups: { day: string; items: AccountNotification[] }[] = [];
  for (const n of items) {
    const day = new Date(n.detected_at).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
    const last = groups.at(-1);
    if (last && last.day === day) last.items.push(n);
    else groups.push({ day, items: [n] });
  }
  return groups;
}

/**
 * Notifications (2026-09-30): every account change the collectors noticed, unread first, with the history on request.
 * Each row opens its tenant. Read state is shared by every admin — marking one read clears it for everyone.
 */
export function NotificationsPage() {
  const queryClient = useQueryClient();
  const [showRead, setShowRead] = useState(false);
  const [kind, setKind] = useState('');
  const list = useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'page', showRead],
    queryFn: () => api.accountNotifications(showRead, 200),
    retry: false,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
  const ack = useMutation({ mutationFn: (id: string) => api.ackAccountNotification(id), onSettled: refresh });
  const ackAll = useMutation({ mutationFn: api.ackAllAccountNotifications, onSettled: refresh });

  const items = useMemo(() => (list.data ?? []).filter((n) => !kind || n.kind === kind), [list.data, kind]);
  const unread = (list.data ?? []).filter((n) => !n.acknowledged_at).length;

  return (
    <RequirePermission permission={PERMISSIONS.analyticsRead} what="Notifications">
      <PageHeader
        title="Notifications"
        subtitle="Account changes the collectors noticed between two runs. Marking one read clears it for every admin."
        actions={unread > 0 && (
          <button
            type="button"
            className="btn btn-ghost"
            disabled={ackAll.isPending}
            onClick={() => ackAll.mutate()}
            title={kind ? 'Clears every unread notification, also the kinds the filter hides.' : undefined}
          >
            {kind ? 'Mark all kinds as read' : 'Mark all as read'}
          </button>
        )}
      />
      <div className="filterbar ops-filters">
        <Select label="Kind" inline value={kind} onChange={setKind} placeholder="All kinds" searchable={false}
          options={Object.entries(KIND_LABELS).map(([value, label]) => ({ value, label }))} />
        <label className="checkbox-row t-label">
          <input type="checkbox" checked={showRead} onChange={(e) => setShowRead(e.target.checked)} />
          Show read ones too
        </label>
      </div>
      <MutationError error={ackAll.error ?? ack.error} />

      <QueryState query={{ ...list, data: list.data ? items : undefined }}>
        {(rows) => rows.length === 0 ? (
          <Section title="Nothing here">
            <p className="t-body muted" style={{ margin: 0 }}>
              {showRead ? 'No account changes recorded for this filter.' : 'Nothing unread. Tick “Show read ones too” for the history.'}
            </p>
          </Section>
        ) : (
          <div className="stack">
            {groupByDay(rows).map((group) => (
              <Section key={group.day} title={group.day} meta={`${group.items.length} change${group.items.length === 1 ? '' : 's'}`}>
                <ul className="attention-list">
                  {group.items.map((n) => (
                    <li key={n.id} className={`attention-item${n.acknowledged_at ? ' is-read' : ''}`}>
                      <span className={`notif-dot${n.acknowledged_at ? '' : ' is-unread'}`} aria-label={n.acknowledged_at ? 'read' : 'unread'} />
                      <div className="attention-text">
                        <Link to={notificationLink(n)} className="t-body">{n.summary}</Link>
                        <span className="t-caption muted" title={formatTimestamp(n.detected_at)}>
                          {n.tenant_name || 'an account'} · {KIND_LABELS[n.kind] ?? n.kind} · {formatWhen(n.detected_at)}
                        </span>
                      </div>
                      {!n.acknowledged_at && (
                        <button type="button" className="btn btn-ghost btn-sm" disabled={ack.isPending} onClick={() => ack.mutate(n.id)}>Mark read</button>
                      )}
                    </li>
                  ))}
                </ul>
              </Section>
            ))}
          </div>
        )}
      </QueryState>
    </RequirePermission>
  );
}
